import axios from 'axios';
import cookie from 'js-cookie';
import Qs from 'qs';
import { message } from 'antd';
import { TOKEN, REFRESH_TOKEN, CONFIG_SERVER } from '../constants/config';
import { actionLogout } from '../../view/system/systemAction';
import { refreshTokens } from '../tokenRefresh';

const request = axios.create();

// Multi-tenancy: every request from the dashboard must carry the user's JWT
// so the API gateway can extract the hospital_id claim and route to the
// correct per-tenant Orthanc.  Many call sites (StudiesService, AdminDashboard,
// ShareStudyDialog, BulkDeleteDialog, ...) use the bare `axios` import rather
// than the `api()` wrapper, and would otherwise hit /orthanc/* anonymously
// — bypassing tenant isolation.  This global interceptor closes that gap.
axios.interceptors.request.use((config) => {
  const token = cookie.get(TOKEN);
  if (token && config.headers && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let isAlreadyFetchingAccessToken = false;
let subscribers = [];
const tokenUrl = '/token';

function onAccessTokenFetched(access_token) {
  subscribers = subscribers.map((callback) => callback(access_token));
  subscribers = [];
}

function addSubscriber(callback) {
  subscribers.push(callback);
}

request.interceptors.request.use(
  (config) => {
    // if (config.url.indexOf(tokenUrl) !== -1) {
    //   delete config.headers.Authorization;
    // }
    return config;
  },
  (error) => {
    return Promise.reject(error.response || { data: {} });
  }
);

// Shared 401-handler used by BOTH the wrapped `request` instance (for api()
// callers) AND the global `axios` (for bare `axios.get/post/...` callers such
// as StudiesService.orthancGet, AdminDashboard, ShareStudyDialog, etc.).
//
// Without this, bare-axios call sites would fail permanently after token
// expiry: the request-side interceptor above attaches the (now-stale) Bearer
// token, the server responds 401, and there was no response-side handler on
// global axios to refresh-and-retry. Symptom: dashboard works fine until the
// user idles past the access-token TTL, then every Orthanc call errors until
// a full page reload (which ran some api()-wrapped call first, refreshing
// the token as a side effect).
//
// Sharing isAlreadyFetchingAccessToken + subscribers across both interceptors
// means concurrent 401s from any source coalesce into a single refresh call
// and all wait for the same new token.
const handleResponseError = (error) => {
  console.log('VANHT-debug-401', error);
  const originalRequest = error.config;
  if (
    (error.response && error.response.status === 401) ||
    !cookie.get(TOKEN)
  ) {
    const refreshToken = cookie.get(REFRESH_TOKEN);
    if (
      refreshToken &&
      !originalRequest._retry &&
      error.config.url.indexOf(tokenUrl) === -1
    ) {
      originalRequest._retry = true;
      if (!isAlreadyFetchingAccessToken) {
        isAlreadyFetchingAccessToken = true;
        // Single-use refresh tokens: refresh under the browser-wide lock
        // shared with the other tabs and the viewer (see tokenRefresh.js).
        refreshTokens()
          .then((accessToken) => {
            isAlreadyFetchingAccessToken = false;
            onAccessTokenFetched(accessToken);
          })
          .catch(() => {
            isAlreadyFetchingAccessToken = false;
            subscribers = [];
            cookie.remove(TOKEN);
            cookie.remove(REFRESH_TOKEN);
            actionLogout();
          });
      }
      const retryOriginalRequest = new Promise((resolve) => {
        addSubscriber((access_token) => {
          originalRequest.headers.Authorization = 'Bearer ' + access_token;
          resolve(axios(originalRequest));
        });
      });
      return retryOriginalRequest;
    } else {
      if (error.config.url.indexOf(tokenUrl) !== -1) {
        if (error.response && error.response.status === 403) {
          message.error('Your account is not allowed to access the system!');
          setTimeout(() => {
            actionLogout();
          }, 1000);
        } else if (error.response && error.response.status === 400) {
          message.error('Sysstem error!');
        }
      } else {
        actionLogout();
      }
      subscribers = [];
      cookie.remove(TOKEN);
      cookie.remove(REFRESH_TOKEN);
    }
  } else {
    return Promise.reject(error?.response || { data: {} });
  }
};

request.interceptors.response.use((response) => response, handleResponseError);
axios.interceptors.response.use((response) => response, handleResponseError);

const api = (options) => {
  let config = {
    baseURL: CONFIG_SERVER.BASE_URL,
    ...options,
    paramsSerializer: (params) =>
      Qs.stringify(params, { arrayFormat: 'repeat' }),
    headers: {
      ...options.headers,
    },
  };
  if (cookie.get(TOKEN)) {
    config.headers.Authorization = `Bearer ${cookie.get(TOKEN)}`;
  }
  return request(config);
};

export default api;
